import { z } from 'zod';
import type { UseQueryResult } from '@tanstack/react-query';

// Base interfaces
export interface ChannelType {
  id: number;
  name: string;
  display_name: string;
  display_order: number;
  provider_id: number;
  version_id: number;
  is_sequence: boolean;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
  price?: number;
  description?: string;
}

export interface Category {
  id: number;
  name: string;
  price?: number;
  description?: string;
}

export interface Plan {
  id: number;
  name: string;
  color_index: number;
  price?: number;
  description?: string;
}

// Channel Number Types
export interface ChannelNumberInput {
  typeId: number;
  number: string;
  is_sequence?: boolean;
  is_default?: boolean;
}

export const channelNumberSchema = z.object({
  typeId: z.number({ error: "Channel type is required" }),
  number: z.string(),
  is_sequence: z.boolean().optional(),
  is_default: z.boolean().optional()
});

export interface ChannelNumber {
  id: number;
  channel_id: number;
  type_id: number;
  number: string;
  is_sequence: boolean;
  is_default: boolean;
  type: {
    id: number;
    name: string;
    display_order: number;
  };
}

// Add a new interface for sequence tracking
export interface SequenceInfo {
  lastNumber: number;
  channelId: number;
}

// Channel Types
export const channelSchema = z.object({
  name: z.string().min(1, "Channel name is required"),
  description: z.string().optional(),
  category_id: z.number().nullable(),
  category_ids: z.array(z.number()).optional(),
  default_category_id: z.number().nullable().optional(),
  plan_ids: z.array(z.number()),
  tag_ids: z.array(z.number()).optional(),
  price: z.number().nullable().optional(),
  numbers: z.array(channelNumberSchema).min(1, "At least one channel number is required"),
  is_local: z.boolean().optional(),
  is_duplicate: z.boolean().optional(),
  linked_provider_id: z.number().nullable().optional()
});

export type ChannelFormData = z.infer<typeof channelSchema>;

export interface Channel {
  id: number;
  name: string;
  provider_id: number;
  category_id: number | null;
  description: string | null;
  plan_id: number | null;
  price: number | null;
  created_at: string;
  updated_at: string;
  numbers: ChannelNumber[];
  plans?: Array<{ plan: Plan } | Plan>;
  category?: Category;
  categories?: Array<{
    category_id: number;
    is_default: boolean;
    category?: Category;
  }>;
  tags?: Array<{
    tag_id: number;
    tag?: {
      id: number;
      name: string;
    };
  }>;
  version_id?: number;
  is_local: boolean;
  is_duplicate: boolean;
  linked_provider_id?: number | null;
  linked_provider?: {
    id: number;
    name: string;
    logo_url?: string;
    type_id?: number;
  };
}

// Utility types for Plan handling
export type PlanItem = Plan | { plan: Plan };

export function getPlanId(plan: PlanItem): number {
  return 'plan' in plan ? plan.plan.id : plan.id;
}

export function getPlanName(plan: PlanItem): string {
  return 'plan' in plan ? plan.plan.name : plan.name;
}

export function getPlanColorIndex(plan: PlanItem): number {
  return 'plan' in plan ? plan.plan.color_index : plan.color_index;
}

export interface ChannelWithRelations {
  id: number;
  name: string;
  provider_id: number;
  version_id: number;
  category_id?: number;
  category?: Category;
  categories?: Array<{
    category_id: number;
    is_default: boolean;
    category?: Category;
  }>;
  numbers: ChannelNumber[];
  plans: {
    plan: Plan;
  }[];
  created_at: string;
}

// Channel Type Schema and Input Types
export const channelTypeSchema = z.object({
  name: z.string().min(1, 'Type name is required'),
  display_order: z.number().default(0)
});

export type CreateChannelTypeInput = z.infer<typeof channelTypeSchema>;

// Channel Input Types
export type CreateChannelInput = {
  name: string;
  providerId: number;
  versionId: number;
  price?: number | null;
  categoryId?: number | null;
  categoryIds?: number[];
  defaultCategoryId?: number | null;
  description?: string | null;
  numbers: ChannelNumberInput[];
  planIds?: number[];
  tagIds?: number[];
  is_local?: boolean;
  is_duplicate?: boolean;
  linked_provider_id?: number | null;
};

export type UpdateChannelInput = {
  id: number;
  name?: string;
  providerId: number;
  versionId: number;
  price?: number | null;
  categoryId?: number | null;
  categoryIds?: number[];
  defaultCategoryId?: number | null;
  description?: string | null;
  numbers?: ChannelNumberInput[];
  planIds?: number[];
  tagIds?: number[];
  categoryUpdateMode?: 'add' | 'replace' | 'remove';
  is_local?: boolean;
  is_duplicate?: boolean;
  linked_provider_id?: number | null;
};

export type UpdateChannelData = Omit<UpdateChannelInput, 'id'>;

// Interface for bulk updating channels
export interface BulkUpdateChannelsInput {
  channelIds: number[];
  providerId: number;
  versionId: number;
  category_id?: number | null;
  category_ids?: number[];
  default_category_id?: number | null;
  categoryUpdateMode?: 'add' | 'replace' | 'remove';
  plan_ids?: number[];
  description?: string;
  price?: number | null;
  is_local?: boolean;
  is_duplicate?: boolean;
  linked_provider_id?: number | null;
}

export interface UpdateChannelTypeInput {
  typeId: number;
  providerId: number;
  versionId: number;
  name: string;
  display_order: number;
  is_default: boolean;
}

// Query Result Types
export interface ChannelTypesQueryResult {
  data: ChannelType[] | null;
  error: unknown;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  status: 'error' | 'success' | 'loading';
  refresh: () => Promise<void>;
}

export type UseChannelTypesResult = UseQueryResult<ChannelType[] | null> & {
  refresh: () => Promise<void>;
};

